import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import DrawerPortal from "@/components/ui/atoms/DrawerPortal";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import { VitalsParameter, RecordReadingDto } from "@/types/vitals";
import styles from "./AddHeightModal.module.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AddHeightModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  currentHeight?: number; // cm — only used in config mode
  mode?: "config" | "reading";
  onSuccess: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cmToFtIn(cm: number): { ft: number; inches: number } {
  const totalInches = cm / 2.54;
  let ft = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches % 12);
  if (inches === 12) {
    ft += 1;
    inches = 0;
  }
  return { ft, inches };
}

function ftInToCm(ft: number, inches: number): number {
  return Math.round((ft * 12 + inches) * 2.54);
}

const backdropVariants = {
  visible: { opacity: 1, pointerEvents: "auto" as const },
  hidden: { opacity: 0, pointerEvents: "none" as const },
};
const drawerVariants = { hidden: { y: "100%" }, visible: { y: 0 } };

// ── Component ─────────────────────────────────────────────────────────────────

const AddHeightModal: React.FC<AddHeightModalProps> = ({
  isOpen,
  onClose,
  memberId,
  currentHeight,
  mode = "config",
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isEdit = mode === "config" && currentHeight != null;

  const [unit, setUnit] = useState<"cm" | "ft">("cm");
  const [cmValue, setCmValue] = useState(() =>
    currentHeight ? String(currentHeight) : ""
  );
  const [ftValue, setFtValue] = useState(() => {
    if (!currentHeight) return "";
    return String(cmToFtIn(currentHeight).ft);
  });
  const [inValue, setInValue] = useState(() => {
    if (!currentHeight) return "";
    return String(cmToFtIn(currentHeight).inches);
  });
  const [recordedAt, setRecordedAt] = useState(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCmValue(currentHeight ? String(currentHeight) : "");
    if (currentHeight) {
      const { ft, inches } = cmToFtIn(currentHeight);
      setFtValue(String(ft));
      setInValue(String(inches));
    } else {
      setFtValue("");
      setInValue("");
    }
    setRecordedAt(format(new Date(), "yyyy-MM-dd"));
    setNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const heightCm =
    unit === "cm"
      ? Number(cmValue)
      : ftInToCm(Number(ftValue), Number(inValue));

  const canSave =
    unit === "cm"
      ? !!cmValue && Number(cmValue) > 0
      : !!ftValue && Number(ftValue) > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (mode === "reading") {
        const dto: RecordReadingDto = {
          parameter: VitalsParameter.HEIGHT,
          values: { value: heightCm },
          recordedAt: new Date(recordedAt),
          note: note.trim() || undefined,
        };
        await vitalsTrackingService.recordReading(memberId, dto);
      } else {
        await vitalsTrackingService.updateCustomRange(memberId, {
          [VitalsParameter.WEIGHT]: { height: heightCm },
        });
      }
      onSuccess();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <DrawerPortal>
          <motion.div
            className={styles.backdrop}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            onClick={onClose}
          />
          <div className={styles.drawerWrapper}>
            <motion.div
              className={styles.drawer}
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={drawerVariants}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {/* Header */}
              <div className={styles.header}>
                <div className={styles.dragBar} />
                <p className={styles.title}>
                  {mode === "reading"
                    ? t("vitalsTracker.height.readingTitle")
                    : isEdit
                    ? t("vitalsTracker.height.editTitle")
                    : t("vitalsTracker.height.addTitle")}
                </p>
                {mode === "config" && (
                  <p className={styles.subtitle}>
                    {t("vitalsTracker.height.subtitle")}
                  </p>
                )}
              </div>

              {/* Fields */}
              <div className={styles.fields}>
                <p className={styles.fieldLabel}>
                  {t("vitalsTracker.height.label")}
                </p>

                <div className={styles.inputRow}>
                  {unit === "cm" ? (
                    <>
                      <input
                        className={styles.input}
                        type="number"
                        inputMode="numeric"
                        placeholder="160"
                        value={cmValue}
                        onChange={(e) => setCmValue(e.target.value)}
                        min={50}
                        max={250}
                      />
                      <span className={styles.unitLabel}>cm</span>
                    </>
                  ) : (
                    <>
                      <input
                        className={styles.input}
                        type="number"
                        inputMode="numeric"
                        placeholder="5"
                        value={ftValue}
                        onChange={(e) => setFtValue(e.target.value)}
                        min={1}
                        max={8}
                      />
                      <span className={styles.unitLabel}>ft</span>
                      <input
                        className={`${styles.input} ${styles.inputIn}`}
                        type="number"
                        inputMode="numeric"
                        placeholder="8"
                        value={inValue}
                        onChange={(e) => setInValue(e.target.value)}
                        min={0}
                        max={11}
                      />
                      <span className={styles.unitLabel}>in</span>
                    </>
                  )}

                  {/* cm / ft·in toggle */}
                  <div className={styles.unitToggle}>
                    <button
                      className={`${styles.unitBtn}${
                        unit === "cm" ? ` ${styles.unitActive}` : ""
                      }`}
                      onClick={() => setUnit("cm")}
                      type="button"
                    >
                      cm
                    </button>
                    <button
                      className={`${styles.unitBtn}${
                        unit === "ft" ? ` ${styles.unitActive}` : ""
                      }`}
                      onClick={() => setUnit("ft")}
                      type="button"
                    >
                      ft / in
                    </button>
                  </div>
                </div>

                {/* When + Note fields — reading mode only */}
                {mode === "reading" && (
                  <>
                    <p className={styles.fieldLabel}>
                      {t("vitalsTracker.height.whenLabel")}
                    </p>
                    <input
                      className={styles.input}
                      type="date"
                      value={recordedAt}
                      onChange={(e) => setRecordedAt(e.target.value)}
                      max={format(new Date(), "yyyy-MM-dd")}
                    />
                    <p className={styles.fieldLabel}>
                      {t("vitalsTracker.readingsDetail.notePlaceholder")}
                    </p>
                    <textarea
                      className={styles.noteInput}
                      placeholder={t(
                        "vitalsTracker.readingsDetail.notePlaceholder"
                      )}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                    />
                  </>
                )}

                {/* Buttons */}
                <div className={styles.buttons}>
                  <button
                    className={styles.cancelBtn}
                    onClick={onClose}
                    type="button"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    className={styles.saveBtn}
                    onClick={handleSave}
                    disabled={!canSave || saving}
                    type="button"
                  >
                    {saving
                      ? t("common.saving")
                      : t("vitalsTracker.height.save")}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </DrawerPortal>
      )}
    </AnimatePresence>
  );
};

export default AddHeightModal;
